import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { api } from '../../services/api';

export interface Comment {
  id: string;
  _id?: string;
  entityType: 'project' | 'projectFile';
  entityId: string;
  userId: string | {
    profile?: {
      displayName?: string;
      nickname?: string;
      firstName?: string;
      lastName?: string;
      avatar?: string;
    };
    email?: string;
  };
  parentId?: string;
  content: string;
  rating?: number;
  edited: boolean;
  editHistory?: Array<{ content: string; editedAt: string }>;
  reactions?: Array<{ userId: string; type: string; createdAt: string }>;
  reactionSummary?: Record<string, number>;
  userReactions?: Record<string, boolean>;
  hidden: boolean;
  hiddenBy?: string;
  hiddenAt?: string;
  hiddenReason?: string;
  createdAt: string;
  updatedAt: string;
}

interface CommentsState {
  byEntity: Record<string, {
    items: Comment[];
    replies: Record<string, Comment[]>;
    pagination: any;
    isLoading: boolean;
    error: string | null;
  }>;
  posting: Record<string, boolean>;
  updating: Record<string, boolean>;
  deleting: Record<string, boolean>;
}

const initialState: CommentsState = {
  byEntity: {},
  posting: {},
  updating: {},
  deleting: {},
};

// Helper to generate entity key
const getEntityKey = (entityType: string, entityId: string) => `${entityType}:${entityId}`;

export const fetchComments = createAsyncThunk(
  'comments/fetch',
  async (
    { entityType, entityId, page = 1, limit = 20, parentId }: {
      entityType: 'project' | 'projectFile';
      entityId: string;
      page?: number;
      limit?: number;
      parentId?: string;
    },
    { getState }
  ) => {
    const state = getState() as { auth: { accessToken: string | null } };
    const token = state.auth.accessToken || undefined;
    const data = await api.comments.getComments(entityType, entityId, { page, limit, parentId }, token);
    return { entityType, entityId, data };
  }
);

export const createComment = createAsyncThunk(
  'comments/create',
  async (
    {
      entityType,
      entityId,
      content,
      parentId,
      rating,
    }: {
      entityType: 'project' | 'projectFile';
      entityId: string;
      content: string;
      parentId?: string;
      rating?: number;
    },
    { getState }
  ) => {
    const state = getState() as { auth: { accessToken: string | null } };
    if (!state.auth.accessToken) {
      throw new Error('Authentication required');
    }
    const comment = await api.comments.createComment(
      entityType,
      entityId,
      { content, parentId, rating },
      state.auth.accessToken
    );
    return { entityType, entityId, comment };
  }
);

export const updateComment = createAsyncThunk(
  'comments/update',
  async (
    { commentId, content, rating }: { commentId: string; content?: string; rating?: number },
    { getState }
  ) => {
    const state = getState() as { auth: { accessToken: string | null } };
    if (!state.auth.accessToken) {
      throw new Error('Authentication required');
    }
    const comment = await api.comments.updateComment(commentId, { content, rating }, state.auth.accessToken);
    return { comment };
  }
);

export const deleteComment = createAsyncThunk(
  'comments/delete',
  async (commentId: string, { getState }) => {
    const state = getState() as { auth: { accessToken: string | null } };
    if (!state.auth.accessToken) {
      throw new Error('Authentication required');
    }
    await api.comments.deleteComment(commentId, state.auth.accessToken);
    return { commentId };
  }
);

export const hideComment = createAsyncThunk(
  'comments/hide',
  async ({ commentId, reason }: { commentId: string; reason?: string }, { getState }) => {
    const state = getState() as { auth: { accessToken: string | null } };
    if (!state.auth.accessToken) {
      throw new Error('Authentication required');
    }
    const comment = await api.comments.hideComment(commentId, reason, state.auth.accessToken);
    return { comment };
  }
);

export const unhideComment = createAsyncThunk(
  'comments/unhide',
  async (commentId: string, { getState }) => {
    const state = getState() as { auth: { accessToken: string | null } };
    if (!state.auth.accessToken) {
      throw new Error('Authentication required');
    }
    const comment = await api.comments.unhideComment(commentId, state.auth.accessToken);
    return { comment };
  }
);

export const addReaction = createAsyncThunk(
  'comments/addReaction',
  async ({ commentId, type }: { commentId: string; type: string }, { getState }) => {
    const state = getState() as { auth: { accessToken: string | null } };
    if (!state.auth.accessToken) {
      throw new Error('Authentication required');
    }
    const data = await api.comments.addReaction(commentId, type, state.auth.accessToken);
    return { commentId, ...data };
  }
);

const commentsSlice = createSlice({
  name: 'comments',
  initialState,
  reducers: {
    addCommentFromWS: (state, action: PayloadAction<{ entityType: string; entityId: string; comment: Comment }>) => {
      const key = getEntityKey(action.payload.entityType, action.payload.entityId);
      if (!state.byEntity[key]) {
        state.byEntity[key] = {
          items: [],
          replies: {},
          pagination: {},
          isLoading: false,
          error: null,
        };
      }
      if (action.payload.comment.parentId) {
        if (!state.byEntity[key].replies[action.payload.comment.parentId]) {
          state.byEntity[key].replies[action.payload.comment.parentId] = [];
        }
        state.byEntity[key].replies[action.payload.comment.parentId].unshift(action.payload.comment);
      } else {
        state.byEntity[key].items.unshift(action.payload.comment);
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchComments.pending, (state, action) => {
        const key = getEntityKey(action.meta.arg.entityType, action.meta.arg.entityId);
        if (!state.byEntity[key]) {
          state.byEntity[key] = {
            items: [],
            replies: {},
            pagination: {},
            isLoading: true,
            error: null,
          };
        } else {
          state.byEntity[key].isLoading = true;
        }
      })
      .addCase(fetchComments.fulfilled, (state, action) => {
        const key = getEntityKey(action.payload.entityType, action.payload.entityId);
        if (!state.byEntity[key]) {
          state.byEntity[key] = {
            items: [],
            replies: {},
            pagination: {},
            isLoading: false,
            error: null,
          };
        }
        state.byEntity[key].items = action.payload.data.comments;
        state.byEntity[key].pagination = action.payload.data.pagination;
        state.byEntity[key].isLoading = false;
        state.byEntity[key].error = null;
      })
      .addCase(fetchComments.rejected, (state, action) => {
        const key = getEntityKey(action.meta.arg.entityType, action.meta.arg.entityId);
        if (state.byEntity[key]) {
          state.byEntity[key].isLoading = false;
          state.byEntity[key].error = action.error.message || 'Failed to fetch comments';
        }
      })
      .addCase(createComment.pending, (state, action) => {
        const key = getEntityKey(action.meta.arg.entityType, action.meta.arg.entityId);
        state.posting[key] = true;
      })
      .addCase(createComment.fulfilled, (state, action) => {
        const key = getEntityKey(action.payload.entityType, action.payload.entityId);
        state.posting[key] = false;
        if (!state.byEntity[key]) {
          state.byEntity[key] = {
            items: [],
            replies: {},
            pagination: {},
            isLoading: false,
            error: null,
          };
        }
        if (action.payload.comment.parentId) {
          if (!state.byEntity[key].replies[action.payload.comment.parentId]) {
            state.byEntity[key].replies[action.payload.comment.parentId] = [];
          }
          state.byEntity[key].replies[action.payload.comment.parentId].unshift(action.payload.comment);
        } else {
          state.byEntity[key].items.unshift(action.payload.comment);
        }
      })
      .addCase(createComment.rejected, (state, action) => {
        const key = getEntityKey(action.meta.arg.entityType, action.meta.arg.entityId);
        state.posting[key] = false;
      })
      .addCase(updateComment.pending, (state, action) => {
        state.updating[action.meta.arg.commentId] = true;
      })
      .addCase(updateComment.fulfilled, (state, action) => {
        state.updating[action.meta.arg.commentId] = false;
        // Update comment in all entities
        Object.keys(state.byEntity).forEach((key) => {
          const entity = state.byEntity[key];
          const index = entity.items.findIndex((c) => c.id === action.payload.comment.id || c._id === action.payload.comment.id);
          if (index !== -1) {
            entity.items[index] = action.payload.comment;
          }
          // Also check replies
          Object.keys(entity.replies).forEach((parentId) => {
            const replyIndex = entity.replies[parentId].findIndex(
              (c) => c.id === action.payload.comment.id || c._id === action.payload.comment.id
            );
            if (replyIndex !== -1) {
              entity.replies[parentId][replyIndex] = action.payload.comment;
            }
          });
        });
      })
      .addCase(updateComment.rejected, (state, action) => {
        state.updating[action.meta.arg.commentId] = false;
      })
      .addCase(deleteComment.pending, (state, action) => {
        state.deleting[action.meta.arg] = true;
      })
      .addCase(deleteComment.fulfilled, (state, action) => {
        state.deleting[action.payload.commentId] = false;
        // Remove comment from all entities
        Object.keys(state.byEntity).forEach((key) => {
          const entity = state.byEntity[key];
          entity.items = entity.items.filter((c) => c.id !== action.payload.commentId && c._id !== action.payload.commentId);
          Object.keys(entity.replies).forEach((parentId) => {
            entity.replies[parentId] = entity.replies[parentId].filter(
              (c) => c.id !== action.payload.commentId && c._id !== action.payload.commentId
            );
          });
        });
      })
      .addCase(deleteComment.rejected, (state, action) => {
        state.deleting[action.meta.arg] = false;
      })
      .addCase(hideComment.fulfilled, (state, action) => {
        // Update comment in all entities
        Object.keys(state.byEntity).forEach((key) => {
          const entity = state.byEntity[key];
          const index = entity.items.findIndex((c) => c.id === action.payload.comment.id || c._id === action.payload.comment.id);
          if (index !== -1) {
            entity.items[index] = action.payload.comment;
          }
          Object.keys(entity.replies).forEach((parentId) => {
            const replyIndex = entity.replies[parentId].findIndex(
              (c) => c.id === action.payload.comment.id || c._id === action.payload.comment.id
            );
            if (replyIndex !== -1) {
              entity.replies[parentId][replyIndex] = action.payload.comment;
            }
          });
        });
      })
      .addCase(unhideComment.fulfilled, (state, action) => {
        // Update comment in all entities
        Object.keys(state.byEntity).forEach((key) => {
          const entity = state.byEntity[key];
          const index = entity.items.findIndex((c) => c.id === action.payload.comment.id || c._id === action.payload.comment.id);
          if (index !== -1) {
            entity.items[index] = action.payload.comment;
          }
          Object.keys(entity.replies).forEach((parentId) => {
            const replyIndex = entity.replies[parentId].findIndex(
              (c) => c.id === action.payload.comment.id || c._id === action.payload.comment.id
            );
            if (replyIndex !== -1) {
              entity.replies[parentId][replyIndex] = action.payload.comment;
            }
          });
        });
      })
      .addCase(addReaction.fulfilled, (state, action) => {
        // Update comment reactions in all entities
        Object.keys(state.byEntity).forEach((key) => {
          const entity = state.byEntity[key];
          const index = entity.items.findIndex((c) => c.id === action.payload.commentId || c._id === action.payload.commentId);
          if (index !== -1) {
            entity.items[index].reactionSummary = action.payload.reactionSummary;
            entity.items[index].userReactions = { [action.payload.userReaction || '']: !!action.payload.userReaction };
          }
          Object.keys(entity.replies).forEach((parentId) => {
            const replyIndex = entity.replies[parentId].findIndex(
              (c) => c.id === action.payload.commentId || c._id === action.payload.commentId
            );
            if (replyIndex !== -1) {
              entity.replies[parentId][replyIndex].reactionSummary = action.payload.reactionSummary;
              entity.replies[parentId][replyIndex].userReactions = { [action.payload.userReaction || '']: !!action.payload.userReaction };
            }
          });
        });
      });
  },
});

export const { addCommentFromWS } = commentsSlice.actions;
export default commentsSlice.reducer;
